import React, { useEffect, useMemo, useState } from 'react';
import { Button, InputGroup, Form, Spinner } from 'react-bootstrap';
import { LanguageProvider } from '../../core/language-provider';
import moment from 'moment';
import { Utils } from '../../core/utils';

const CUSTOMER_ORDER_STATUSES = [
    { value: '0_PENDING', variant: 'primary' },
    { value: '1_ORDERED', variant: 'secondary' },
    { value: '7_READY_FOR_DELIVERY', variant: 'info' },
    { value: '15_PARTIAL_DELIVERED', variant: 'warning' },
    { value: '10_COMPLETED', variant: 'success' }
];

const ENTITY_STATUS_CONFIG = {
    customer_order: {
        resetVariant: 'outline-danger',
        statuses: CUSTOMER_ORDER_STATUSES
    },
    gyv_customer_order: {
        resetVariant: 'outline-danger',
        statuses: CUSTOMER_ORDER_STATUSES
    },
    customer_offer: {
        resetVariant: 'outline-danger',
        statuses: [
            { value: '0_PENDING', variant: 'primary' },
            { value: '10_CONFIRMED', variant: 'success' },
            { value: '20_DECLINED', variant: 'danger' }
        ]
    },
    gyv_customer_offer: {
        resetVariant: 'outline-danger',
        statuses: [
            { value: '0_PENDING', variant: 'primary' },
            { value: '10_CONFIRMED', variant: 'success' },
            { value: '20_DECLINED', variant: 'danger' }
        ]
    },
    gyv_customer_order_delivery: {
        resetVariant: 'outline-danger',
        statuses: [
            { value: '10_CREATED', variant: 'primary' },
            { value: '20_DELIVERED', variant: 'success' }
        ]
    },
    supplier_order: {
        resetVariant: 'outline-danger',
        statuses: [
            { value: '1_TO_SEND', variant: 'secondary' },
            { value: '2_SENT', variant: 'primary' },
            { value: '5_PARTIAL', variant: 'warning' },
            { value: '10_COMPLETED', variant: 'success' }
        ]
    },
    gyv_supplier_order: {
        resetVariant: 'outline-danger',
        statuses: [
            { value: '1_TO_SEND', variant: 'secondary' },
            { value: '2_SENT', variant: 'primary' },
            { value: '5_PARTIAL', variant: 'warning' },
            { value: '10_COMPLETED', variant: 'success' }
        ]
    },
    invoice: {
        resetVariant: 'danger',
        statuses: [
            { value: '0_PENDING', variant: 'secondary' },
            { value: '10_CONFIRMED', variant: 'success' }
        ]
    },
    gyv_invoice: {
        resetVariant: 'danger',
        statuses: [
            { value: '0_PENDING', variant: 'secondary' },
            { value: '10_CONFIRMED', variant: 'success' }
        ]
    }
};


export default function DataGridTopHook({ entity, data, setData, searchObj, setSearchObj }) {

    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState('');

    const [activeStatus, setActiveStatus] = useState(null);

    const statusConfig = ENTITY_STATUS_CONFIG[entity];

    const statusButtons = useMemo(() => {
        if (!statusConfig) {
            return [];
        }

        return statusConfig.statuses.map(({ value, variant }) => ({
            value,
            variant,
            label: LanguageProvider.getLabel(entity, `status.options.${value}`) || value
        }));
    }, [entity, statusConfig]);

    useEffect(() => {
        if (!statusConfig) {
            return;
        }

        const constraints = (searchObj && Array.isArray(searchObj.constraints)) ? searchObj.constraints : [];
        const statusConstraint = constraints.find((item) => item.fieldName === 'status' && item.type === 'value');
        const nextActive = statusConstraint ? statusConstraint.value : null;

        setActiveStatus((prev) => (prev !== nextActive ? nextActive : prev));
    }, [entity, searchObj, statusConfig]);

    const handleStatusClick = (statusValue) => {
        if (typeof setSearchObj !== 'function') {
            return;
        }

        const baseSearchObj = searchObj || { query: '', fields: [], constraints: [] };
        const currentConstraints = Array.isArray(baseSearchObj.constraints) ? baseSearchObj.constraints : [];
        const constraintsWithoutStatus = currentConstraints.filter((item) => item.fieldName !== 'status');

        const nextStatus = statusValue === activeStatus ? null : statusValue;

        const nextConstraints = nextStatus
            ? [...constraintsWithoutStatus, { fieldName: 'status', type: 'value', value: nextStatus }]
            : constraintsWithoutStatus;

        const nextSearchObj = {
            ...baseSearchObj,
            constraints: nextConstraints
        };

        setActiveStatus(nextStatus);
        setSearchObj(nextSearchObj);
    };

    const handleResetClick = () => {
        if (typeof setSearchObj !== 'function') {
            return;
        }

        const baseSearchObj = searchObj || { query: '', fields: [], constraints: [] };
        const nextSearchObj = {
            ...baseSearchObj,
            constraints: []
        };

        setActiveStatus(null);
        setSearchObj(nextSearchObj);
    };

    const resetLabel = LanguageProvider.get('defaults.reset') || 'Tutti';

    const renderStatusButtons = () => {
        if (!statusConfig) {
            return <></>;
        }

        if (!statusButtons.length) {
            return <></>;
        }

        const resetVariant = statusConfig.resetVariant || 'primary';

        return (
            <div className='mb-3 d-flex flex-wrap align-items-center gap-2'>
                <Button
                    size='sm'
                    variant={activeStatus === null ? resetVariant : `outline-${resetVariant}`}
                    onClick={handleResetClick}
                    active={activeStatus === null}
                >
                    {resetLabel}
                </Button>
                {statusButtons.map((status) => {
                    const isActive = activeStatus === status.value;
                    const baseVariant = status.variant || 'secondary';
                    const variant = isActive ? baseVariant : `outline-${baseVariant}`;

                    return (
                        <Button
                            key={status.value}
                            size='sm'
                            variant={variant}
                            onClick={() => handleStatusClick(status.value)}
                            active={isActive}
                        >
                            {status.label}
                        </Button>
                    );
                })}
            </div>
        );
    };



    function filterByDate() {
        if (dateFrom && dateTo) {
            var s = Utils.cloneObject(searchObj)
            var c = s.constraints
            var ct = []

            for (var i = 0; i < c.length; i++) {
                if (c[i].fieldName !== 'date') {
                    ct.push(c[i])
                }
            }
            ct.push({
                fieldName: 'date',
                type: 'greater',
                value: moment(dateFrom, 'YYYY-MM-DD').format('YYYY-MM-DDT00:00')
            })
            ct.push({
                fieldName: 'date',
                type: 'minor',
                value: (moment(dateTo, 'YYYY-MM-DD').add(1, 'days')).format('YYYY-MM-DDT00:00')
            })

            s.constraints = ct
            setSearchObj(s)
        }
        else {
            setErrors('Seleziona le date per procedere.')
            setTimeout(() => {
                setErrors('')
            }, "3000")
        }
    }


    if (entity !== null) {
        return (
            <>
                {statusConfig && renderStatusButtons()}
                {(() => {
                    switch (entity) {
                        case 'sample':
                            return (
                                <span>Test</span>
                            );
                        case 'customer':
                            return (
                                <>
                                    <InputGroup className="mt-2 mb-3" style={{ maxWidth: 500 }}>
                                        <Form.Control type="date" onChange={(event) => setDateFrom(event.target.value)} />
                                        <Form.Control type="date" onChange={(event) => setDateTo(event.target.value)} />
                                        {loading ? (
                                            <Button size='md' variant='success' disabled><Spinner /></Button>
                                        ) : (
                                            <Button size='md' variant='success' onClick={() => filterByDate()}>Filtra per data creazione</Button>
                                        )}
                                    </InputGroup>
                                </>
                            );
                        case 'gyv_customer_order':
                            return (
                                <>
                                    <InputGroup className="mt-2 mb-3" style={{ maxWidth: 500 }}>
                                        <Form.Control type="date" onChange={(event) => setDateFrom(event.target.value)} />
                                        <Form.Control type="date" onChange={(event) => setDateTo(event.target.value)} />
                                        {loading ? (
                                            <Button size='md' variant='success' disabled><Spinner /></Button>
                                        ) : (
                                            <Button size='md' variant='success' onClick={() => filterByDate()}>Filtra per data creazione</Button>
                                        )}
                                    </InputGroup>
                                </>
                            );
                        default:
                            return (
                                <></>
                            );
                    }
                })()}
            </>
        )
    }
    else {
        return (
            <></>
        )
    }
};
